import { graphql, navigate, useStaticQuery } from 'gatsby'
import React, { useState } from 'react'
import { ThreeDots } from 'react-loader-spinner'
import { FormRow, InputType } from './form-row'
import { getCookie } from '../util/helpers'

/* The contact form for /contact/message/.

   WHY THIS IS NOT `demo-form.tsx` WITH DIFFERENT LABELS. That component's success
   path navigates to `/demo/calendar/` with the submitted values in the query
   string, because booking a demo is a two-step funnel. A general enquiry has
   nowhere to go next, so it resolves in place. The field sets also differ: the
   demo form needs Country (it routes the lead by region) and has no message
   field, which is the one thing a contact form cannot do without.

   WHY IT IS NOT `contact-us-form.tsx` EITHER. That is the global modal, and it
   posts to NETLIFY FORMS, not HubSpot — so nothing submitted through it reaches
   the CRM or is attributed to a visitor session. This component posts to the
   HubSpot Forms API the way the demo form does, including the `hubspotutk` cookie
   so the submission attaches to the visitor's existing HubSpot timeline.

   TWO PIPES, ONE ON PURPOSE. Until a HubSpot contact form GUID exists in
   `gatsby-config.mjs`, this falls back to the Netlify `contact-us` form that the
   modal already registers at build time. That keeps the page working today and
   upgrades the moment the GUID lands — rather than shipping a form that silently
   throws.

   SUCCESS NAVIGATES TO `/contact/thank-you/` rather than swapping the form for a
   confirmation panel. Two reasons: a real URL is what analytics and any HubSpot or
   Google Ads conversion goal can fire on, which an in-place state cannot; and it
   matches the demo funnel, which ends on `/demo/confirm/`. */

interface StaticQueryData {
  site: {
    siteMetadata: {
      siteUrl: string
      hubspotConfig: {
        portalId: string
        contactFormId: string
      }
    }
  }
}

const defaultFormData = {
  company: '',
  firstname: '',
  lastname: '',
  email: '',
  message: '',
  consent: false,
}

const ContactPageForm = () => {
  const {
    site: {
      siteMetadata: { siteUrl, hubspotConfig },
    },
  }: StaticQueryData = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          siteUrl
          hubspotConfig {
            portalId
            contactFormId
          }
        }
      }
    }
  `)

  const [
    { company, firstname, lastname, email, message, consent },
    setFormData,
  ] = useState(defaultFormData)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  > = event => {
    const { target } = event
    const value =
      target.type === 'checkbox'
        ? (target as HTMLInputElement).checked
        : target.value
    setFormData(formData => ({ ...formData, [target.name]: value }))
    if (error) setError('')
  }

  const postToHubspot = async () => {
    const data = {
      submittedAt: Date.now(),
      fields: [
        { objectTypeId: '0-1', name: 'company', value: company },
        { objectTypeId: '0-1', name: 'firstname', value: firstname },
        { objectTypeId: '0-1', name: 'lastname', value: lastname },
        { objectTypeId: '0-1', name: 'email', value: email },
        { objectTypeId: '0-1', name: 'message', value: message },
        {
          objectTypeId: '0-1',
          name: 'you_consent_to_cognitive3d_processing_your_personal_data_in_accordance_with_our_privacy_policy',
          value: true,
        },
      ],
      context: {
        // The HubSpot tracking cookie. Without it the submission creates an
        // orphan contact instead of attaching to the visitor's existing timeline.
        hutk: getCookie('hubspotutk'),
        pageUri: `${siteUrl}${location.pathname}`,
        pageName: 'Contact',
      },
    }

    const result = await fetch(
      `https://api.hsforms.com/submissions/v3/integration/submit/${hubspotConfig.portalId}/${hubspotConfig.contactFormId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
    )
    return result.ok && result.status === 200
  }

  const postToNetlify = async () => {
    // `form-name: contact-us` is the form `contact-us-form.tsx` registers with
    // `data-netlify="true"`, so Netlify already knows this name at build time.
    // Posting urlencoded to `/` is the same mechanism `form-utils.ts` uses.
    const body = new URLSearchParams({
      'form-name': 'contact-us',
      company_name: company,
      first_name: firstname,
      last_name: lastname,
      email,
      message,
      consent: 'true',
    }).toString()

    const result = await fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    })
    return result.status === 200
  }

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!consent) {
      setError('Please agree to the privacy policy before sending.')
      return
    }

    setSubmitting(true)
    setError('')
    try {
      /* HUBSPOT FIRST, NETLIFY AS A SAFETY NET — and the net catches a FAILED
         HubSpot post, not just a missing form ID.

         The earlier version only fell back when `contactFormId` was empty. That
         left a real hole: the Forms API rejects a submission containing a field
         the form definition does not have, so any drift between this component's
         field list and the HubSpot form — a field renamed, a form edit saved but
         not published — would drop the enquiry entirely and show the visitor an
         error. Now a rejection re-posts to the Netlify pipe the contact modal
         already uses, so the message still reaches the team while the mismatch
         gets fixed.

         The console warning is deliberate: a silent fallback would hide exactly
         the misconfiguration someone needs to know about. */
      let ok = false
      if (hubspotConfig.contactFormId) {
        ok = await postToHubspot()
        if (!ok) {
          console.warn(
            '[contact form] HubSpot submission was rejected — falling back to Netlify. Check that the HubSpot form’s fields match this component and that its changes are PUBLISHED.',
          )
          ok = await postToNetlify()
        }
      } else {
        ok = await postToNetlify()
      }
      if (ok) {
        // No `setSubmitting(false)` on this path on purpose: the spinner stays up
        // through the navigation, so a slow route transition cannot leave a live
        // Send button that resubmits the message.
        navigate('/contact/thank-you/')
        return
      }
      setError('Something went wrong sending your message. Please try again.')
    } catch (err) {
      console.log({ err })
      setError('Something went wrong sending your message. Please try again.')
    }
    setSubmitting(false)
  }

  return (
    /* `demo-form` is deliberate, not a copy-paste leftover: the entire field skin
       (50px inputs, #faf9fe fill, hairline border, purple focus ring,
       placeholder-as-label, the shine-sweep submit) lives on
       `.demo-dark .demo-form` in `_demo.scss`. Carrying the class inherits it. */
    <form className="demo-form contact-form" onSubmit={onSubmit}>
      {/* SUPPLIED AND APPROVED COPY (field labels). Do not rewrite.

          PLACEHOLDER-ONLY, NO LABELS, matching the demo form — stacking a label
          above every placeholder made that card ~110px taller for no added
          clarity. First and Last name are `halfSize` so they share one row. */}
      <FormRow
        halfSize
        inputType={InputType.text}
        inputName={'firstname'}
        autoComplete="given-name"
        required
        placeholder="First name*"
        value={firstname}
        onChange={handleChange}
      />
      <FormRow
        halfSize
        inputType={InputType.text}
        inputName={'lastname'}
        autoComplete="family-name"
        required
        placeholder="Last name*"
        value={lastname}
        onChange={handleChange}
      />
      <FormRow
        halfSize={false}
        inputType={InputType.text}
        inputName={'company'}
        autoComplete="organization"
        required
        placeholder="Company or organization*"
        value={company}
        onChange={handleChange}
      />
      <FormRow
        halfSize={false}
        inputType={InputType.email}
        inputName={'email'}
        autoComplete="email"
        required
        placeholder="Work email*"
        value={email}
        onChange={handleChange}
      />
      <FormRow
        halfSize={false}
        inputType={InputType.textarea}
        inputName={'message'}
        required
        rows={5}
        placeholder="How can we help?*"
        value={message}
        onChange={handleChange}
      />
      <FormRow
        halfSize={false}
        label={
          <>
            <span>I have read and agree to&nbsp;</span>
            {/* The anchor sits inside the checkbox <label>, so a bare click is
                consumed by the label's activation behaviour (it toggles the box)
                instead of following the link. Stop it at the anchor. */}
            <a
              href="/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >
              Privacy Policy.
            </a>
          </>
        }
        inputType={InputType.checkbox}
        inputName={'consent'}
        required
        checked={consent}
        onChange={handleChange}
      />

      {!!error && (
        <p className="contact-form__error" role="alert">
          {error}
        </p>
      )}

      <div className="demo-form__submit-wrapper">
        <button
          type="submit"
          className="btn demo-form__submit"
          disabled={submitting}
        >
          {submitting ? (
            <ThreeDots color="#ffffff" height={18} width={50} />
          ) : (
            `Send message`
          )}
        </button>
      </div>
    </form>
  )
}

export default ContactPageForm
